import React, { useMemo, useState } from 'react';
import { Bell, CheckCheck, ChevronRight, Megaphone, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useLocalization } from '../context/LocalizationContext';
import { useNotifications } from '../context/NotificationsContext';
import { cacheUtils } from '../utils/cache';

const FILTER_OPTIONS = ['ALL', 'UNREAD', 'BROADCAST', 'MEMBER'];

const resolveSeverityClasses = (severity = 'info') => {
  switch (String(severity || '').toLowerCase()) {
    case 'critical':
      return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-200 dark:border-red-900/40';
    case 'important':
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-900/40';
    default:
      return 'bg-primary-100 text-primary-800 border-primary-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700';
  }
};

export const Notifications = () => {
  const navigate = useNavigate();
  const { locale, needsCompactFont, t } = useLocalization();
  const { notifications, unreadCount, loading, error, markAllAsRead, markAsRead, markAsUnread, refreshNotifications } = useNotifications();
  const [activeFilter, setActiveFilter] = useState('ALL');

  React.useEffect(() => {
    void refreshNotifications();
  }, [refreshNotifications]);

  const filteredNotifications = useMemo(() => {
    if (activeFilter === 'UNREAD') {
      return notifications.filter((item) => !item.isRead);
    }
    if (activeFilter === 'BROADCAST') {
      return notifications.filter((item) => item.scope === 'broadcast');
    }
    if (activeFilter === 'MEMBER') {
      return notifications.filter((item) => item.scope === 'member');
    }
    return notifications;
  }, [activeFilter, notifications]);

  const openAction = (notification) => {
    void markAsRead(notification.id);
    if (notification.actionPath) {
      navigate(notification.actionPath);
    }
  };

  return (
    <DashboardLayout>
      <div className="app-page-shell max-w-5xl fade-in" data-testid="notifications-page">
        <div className="pointer-events-none absolute -z-10 inset-0 dark:hidden">
          <div className="absolute -top-10 left-4 h-48 w-48 rounded-full bg-primary-200/30 blur-3xl" />
          <div className="absolute top-24 right-6 h-48 w-48 rounded-full bg-primary-200/25 blur-3xl" />
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className={`app-page-heading ${needsCompactFont ? 'app-page-heading-te' : ''}`}>
              {t('notifications.title')}
            </h1>
            <p className="app-page-subtitle">{t('notifications.subtitle')}</p>
            <p className="app-page-meta">
              {t('notifications.unreadCount', { count: unreadCount, suffix: unreadCount === 1 ? '' : 's' })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void markAllAsRead(filteredNotifications.map((item) => item.id))}
            disabled={loading || filteredNotifications.length === 0}
            className="app-primary-button w-full sm:w-auto self-start"
          >
            <CheckCheck size={16} />
            <span>{t('notifications.markVisibleRead')}</span>
          </button>
        </div>

        {error && (
          <div className="app-highlight-surface border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-200">
            {error}
          </div>
        )}

        <div className="app-highlight-surface p-3 sm:p-4">
          <div className="flex flex-wrap gap-2">
            {FILTER_OPTIONS.map((option) => {
              const active = activeFilter === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => setActiveFilter(option)}
                  className={`rounded-full px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm font-semibold transition-colors ${
                    active
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'bg-primary-100 text-primary-800 hover:bg-primary-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {t(`notifications.filters.${option.toLowerCase()}`)}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="app-surface p-8 sm:p-10 text-center">
            <Bell className="mx-auto h-12 w-12 sm:h-14 sm:w-14 text-primary-300 dark:text-primary-600" strokeWidth={1.5} />
            <h2 className="mt-4 font-serif text-xl sm:text-2xl font-semibold text-primary-900 dark:text-white">
              {t('notifications.loading')}
            </h2>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="app-surface p-8 sm:p-10 text-center">
            <Bell className="mx-auto h-12 w-12 sm:h-14 sm:w-14 text-primary-300 dark:text-primary-600" strokeWidth={1.5} />
            <h2 className="mt-4 font-serif text-xl sm:text-2xl font-semibold text-primary-900 dark:text-white">
              {t('notifications.emptyTitle')}
            </h2>
            <p className="mt-2 text-sm text-primary-700 dark:text-primary-300">
              {t('notifications.emptySubtitle')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredNotifications.map((notification) => {
              const isBroadcast = notification.scope === 'broadcast';
              return (
                <div
                  key={notification.id}
                  className={`app-surface p-4 sm:p-5 ${notification.isRead ? '' : 'ring-1 ring-primary-200 dark:ring-primary-900/40'}`}
                >
                  <div className="flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${resolveSeverityClasses(notification.severity)}`}
                        >
                          {t(`notifications.severity.${String(notification.severity || 'info').toLowerCase()}`)}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-800 dark:bg-slate-800 dark:text-slate-200">
                          {isBroadcast ? <Megaphone size={12} /> : <UserRound size={12} />}
                          {isBroadcast ? t('notifications.scope.broadcast') : t('notifications.scope.member')}
                        </span>
                        {!notification.isRead && (
                          <span className="inline-flex items-center rounded-full bg-primary-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
                            {t('notifications.unread')}
                          </span>
                        )}
                      </div>

                      <h2 className="mt-3 font-serif text-xl sm:text-2xl font-semibold text-primary-900 dark:text-white">
                        {notification.title}
                      </h2>
                      <p className="mt-2 text-sm leading-6 text-primary-800 dark:text-primary-200">
                        {notification.message}
                      </p>
                      <p className="mt-3 text-xs font-medium uppercase tracking-[0.16em] text-primary-500 dark:text-primary-400">
                        {cacheUtils.getTimeAgo(notification.createdAt, locale, t)}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-2">
                      {notification.actionPath && (
                        <button
                          type="button"
                          onClick={() => openAction(notification)}
                          className="app-primary-button w-full sm:w-auto"
                        >
                          <span>{notification.actionLabel || t('notifications.actions.openItem')}</span>
                          <ChevronRight size={16} />
                        </button>
                      )}
                      {notification.isRead ? (
                        <button
                          type="button"
                          onClick={() => void markAsUnread(notification.id)}
                          className="app-secondary-button w-full sm:w-auto"
                        >
                          {t('notifications.markUnread')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void markAsRead(notification.id)}
                          className="app-secondary-button w-full sm:w-auto"
                        >
                          {t('notifications.markRead')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Notifications;
